import { useEffect, useState, useCallback } from "react";
import { Loader2, Mail, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";

export default function GuardianDonationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [campaignFilter, setCampaignFilter] = useState("");
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const params = { limit: 100 };
    if (campaignFilter) params.campaign = campaignFilter;
    const { data } = await api.get("/guardian/donations", { params });
    setItems(data.items);
    setLoading(false);
  }, [campaignFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleMarkTest = async (id) => {
    try {
      await api.patch(`/guardian/donations/${id}/mark-test`);
      toast({ title: "Marked as test" });
      fetchItems();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not mark as test", description: err.response?.data?.detail });
    }
  };

  const handleResend = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/guardian/donations/${id}/resend-receipt`);
      toast({ title: "Receipt resent" });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not resend receipt", description: err.response?.data?.detail });
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (id, receiptNumber) => {
    const response = await api.get(`/guardian/donations/${id}/receipt.pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${receiptNumber.replace(/\//g, "-")}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-3">
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          data-testid="guardian-donations-campaign-filter"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All campaigns</option>
          <option value="diwali2026">Diwali 2026</option>
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      ) : items.length === 0 ? (
        <p className="text-amtm-textMuted text-center py-12" data-testid="guardian-donations-empty">No Guardian donations yet.</p>
      ) : (
        <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="guardian-donations-list">
          {items.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-4 gap-3 flex-wrap" data-testid={`guardian-donation-${d.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-amtm-text flex items-center gap-2">
                  ₹{d.amount.toLocaleString("en-IN")} &middot; {d.type === "recurring_charge" ? "Monthly" : "One-time"}
                  {d.campaign && <Badge variant="outline" data-testid={`guardian-donation-campaign-${d.id}`}>{d.campaign}</Badge>}
                  {d.is_test && <Badge variant="destructive" data-testid={`guardian-donation-test-badge-${d.id}`}>TEST</Badge>}
                </p>
                <p className="text-sm text-amtm-textMuted">{d.receipt_number} &middot; {formatDate(d.created_at)}</p>
                {d.receipt_sent_at ? (
                  <p className="text-xs text-amtm-success">Receipt emailed {formatDate(d.receipt_sent_at)}</p>
                ) : (
                  <p className="text-xs text-amtm-textMuted">Receipt not yet emailed</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleDownload(d.id, d.receipt_number)} data-testid={`guardian-donation-download-${d.id}`}>
                  <FileDown className="h-4 w-4 mr-1.5" /> PDF
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === d.id} onClick={() => handleResend(d.id)} data-testid={`guardian-donation-resend-${d.id}`}>
                  {busyId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
                  Resend
                </Button>
                {!d.is_test && (
                  <Button size="sm" variant="outline" onClick={() => handleMarkTest(d.id)} data-testid={`guardian-donation-mark-test-${d.id}`}>
                    Mark as test
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
